// src/components/chat/chatCenter.jsx
// New, clean message center modal that mirrors MessageCenter.jsx styling/behavior,
// but uses ONLY the new chat logic (matrixStore + ChatList/ChatView).
// Props-based open/close so we don't depend on the old messaging store.
//
// Usage example (later):
//   <ChatCenter isOpen={isOpen} onClose={close} />

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Rnd } from 'react-rnd';
import { FaTimes, FaWindowMaximize, FaWindowRestore } from 'react-icons/fa';
import { useMatrixStore } from '../../state/matrixStore';
import { useMessagingStore } from '../../state/messagingStore';
import ChatWorld from './ChatWorld';
import { useModalStore } from '../../state/modalStore';


export default function ChatCenter({ isOpen, onClose }) {
  const ingestPendingInvites = useMessagingStore(s => s.ingestPendingInvites);
  const ingestSentInvites = useMessagingStore(s => s.ingestSentInvites);
  const modalRef = useRef(null);

  const bringToFront = useModalStore(s => s.bringToFront);
  const chatZ = useModalStore(s => s.getZIndex('chat'));

  const [windowMode, setWindowMode] = useState('norm');

  const [windowRect, setWindowRect] = useState(() => {
    const pad = 12;
    const w = Math.min(800, Math.floor(window.innerWidth * 0.85));
    const h = Math.floor(window.innerHeight * 0.85);
    return {
      width: w,
      height: h,
      x: Math.max(pad, Math.floor((window.innerWidth - w) / 2)),
      y: Math.max(pad, Math.floor((window.innerHeight - h) / 2)),
    };
  });

  const getMaxRect = useCallback(() => {
    const pad = 12;
    return {
      x: pad,
      y: pad,
      width: Math.max(600, window.innerWidth - pad * 2),
      height: Math.max(400, window.innerHeight - pad * 2),
    };
  }, []);

  const toggleMaximize = useCallback(() => {
    setWindowMode((prev) => (prev === 'max' ? 'norm' : 'max'));
  }, []);

  const onMinimizeClick = useCallback(() => {
    if (windowMode === 'max') setWindowMode('norm');
  }, [windowMode]);

  // Read global readiness from the matrixStore (navbar already boots the client)
  const connectionState = useMatrixStore(s => s.connectionState); // e.g. 'disconnected' | 'syncing' | 'connected'
  const isReady = connectionState && connectionState !== 'disconnected';
  const error = null; // keep the UI branch; populate if your store tracks errors

  // NEW — load unified conversation list when ChatCenter opens
  useEffect(() => {
    if (isOpen) {
      try {
        const store = require('../../state/conversationStore').useConversationStore;
        store.getState().loadConversations();
      } catch (err) {
        console.error('[ChatCenter] Failed auto-load conversations:', err);
      }
    }
  }, [isOpen]);

  // Event-driven conversation refresh: subscribe to matrixStore changes
  // (new messages, room updates) and debounce-reload the conversation list.
  // A 30s fallback poll covers DB-only changes (invite state) not visible in Matrix.
  useEffect(() => {
    if (!isOpen) return;

    const store = require('../../state/conversationStore').useConversationStore;
    let refreshTimer = null;

    const debouncedRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        store.getState().loadConversations().catch((err) => {
          console.error('[ChatCenter] Conversation refresh failed:', err);
        });
      }, 1000);
    };

    // Subscribe to matrixStore room/message changes
    const unsubscribe = useMatrixStore.subscribe(
      (state) => state.rooms,
      () => debouncedRefresh()
    );

    // Fallback poll at 30s for DB-only changes (invite accept/decline)
    const fallbackInterval = setInterval(() => {
      store.getState().loadConversations().catch(() => {});
    }, 30000);

    return () => {
      unsubscribe();
      clearInterval(fallbackInterval);
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [isOpen]);


  // Fetch pending DM invites for the logged-in user (invitee side) when the modal opens
useEffect(() => {
  if (!isOpen) return;

  let isCancelled = false;

  const fetchInvites = async () => {
    if (isCancelled) return;

    // 1) Pending invites where I am the invitee
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/dm/pending', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('[ChatCenter] /api/dm/pending HTTP error:', res.status, text);
      } else {
        const data = await res.json().catch(() => null);
        if (!data || !data.ok || !Array.isArray(data.invites)) {
          console.error('[ChatCenter] /api/dm/pending bad payload:', data);
        } else {
          ingestPendingInvites(data.invites);
        }
      }
    } catch (err) {
      console.error('[ChatCenter] Error fetching pending invites:', err);
    }

    // 2) Sent invites where I am the inviter
      try {
        const sentToken = localStorage.getItem('token');
        const res = await fetch('/api/dm/sent', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(sentToken && { Authorization: `Bearer ${sentToken}` }),
          },
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          console.error('[ChatCenter] /api/dm/sent HTTP error:', res.status, text);
        } else {
          const data = await res.json().catch(() => null);
          if (!data || !data.ok || !Array.isArray(data.invites)) {
            console.error('[ChatCenter] /api/dm/sent bad payload:', data);
          } else {
            ingestSentInvites(data.invites);
          }
        }
      } catch (err) {
        console.error('[ChatCenter] Error fetching sent invites:', err);
      }

  };

  // Initial load as soon as the modal opens
  fetchInvites();

  // Reduced fallback poll (30s) — invite state changes are DB-only, not Matrix events.
  // Immediate refresh after user actions (accept/decline) is handled in ChatWorld.
  const intervalId = setInterval(fetchInvites, 30000);

  return () => {
    isCancelled = true;
    clearInterval(intervalId);
  };
}, [isOpen, ingestPendingInvites, ingestSentInvites]);



  // Close on ESC (identical behavior)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Prevent scroll bleed while open (identical behavior)
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    bringToFront('chat');
  }, [isOpen, bringToFront]);

  if (!isOpen) return null;


  return (
    <div
      style={{ zIndex: chatZ }}
      className="fixed inset-0 flex items-center justify-center pointer-events-none"
      aria-modal="true"
      role="dialog"
    >

      <Rnd
        cancel=".eh-chat-nodrag"
        onMouseDownCapture={() => bringToFront('chat')}
        size={
          windowMode === 'max'
            ? { width: getMaxRect().width, height: getMaxRect().height }
            : { width: windowRect.width, height: windowRect.height }
        }
        position={
          windowMode === 'max'
            ? { x: getMaxRect().x, y: getMaxRect().y }
            : { x: windowRect.x, y: windowRect.y }
        }
        minWidth={600}
        minHeight={400}
        bounds="window"
        dragHandleClassName="eh-msg-header"
        disableDragging={windowMode === 'max'}
        enableResizing={
          windowMode === 'max'
            ? false
            : {
                bottomRight: true,
                bottomLeft: true,
                topRight: true,
                topLeft: true,
                left: true,
                right: true,
                bottom: true,
                top: true,
              }
        }
        onDragStop={(e, d) => {
          if (windowMode === 'max') return;
          setWindowRect((prev) => ({
            ...(prev || {}),
            x: d.x,
            y: d.y,
          }));
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          if (windowMode === 'max') return;
          setWindowRect({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
            x: position.x,
            y: position.y,
          });
        }}
        className="rounded-2xl shadow-2xl overflow-hidden flex flex-col bg-white pointer-events-auto"

      >

        <div
          ref={modalRef}
          className="relative w-full h-full flex flex-col"
        >
          {/* Header — identical styling */}
          <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-1 border-b border-[#00aef7] bg-[#00aef7] text-white cursor-move eh-msg-header">
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold tracking-tight">Chats</h2>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleMaximize}
                className="eh-chat-nodrag w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors"
                title={windowMode === 'max' ? 'Restore' : 'Maximize'}
              >
                {windowMode === 'max' ? <FaWindowRestore size={12} /> : <FaWindowMaximize size={12} />}
              </button>
              <button
                onClick={onClose}
                className="eh-chat-nodrag w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors"
                title="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>

          </div>

          {/* Content — identical 1/3 : 2/3 split */}
{/* NEW: Unified ChatWorld replaces ChatList + ChatView */}
<div className="flex-1 min-h-0 flex">
  <ChatWorld />
</div>

        </div>
      </Rnd>
    </div>
  );
}
